/**
 * Traceability links panel (#545, lifecycle-doc-model R3).
 *
 * Design docs: pick the requirement(s) this design satisfies, and remove
 * links. Requirements: read-only "Satisfied by" list of the design docs
 * that link to them. Edges are visible in the relationship graph too.
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Link2, Plus, X, AlertTriangle } from 'lucide-react';
import { Spinner } from '@systemdox/ui';
import {
  specsApi,
  type DocType,
  type SpecEdge,
  type SpecSummary,
} from '@/services/specsApi';
import { cn } from '@/lib/utils';

export function SpecLinksPanel({ draftId, docType }: { draftId: string; docType: DocType }) {
  const [outgoing, setOutgoing] = useState<SpecEdge[]>([]);
  const [incoming, setIncoming] = useState<SpecEdge[]>([]);
  const [loading, setLoading] = useState(true);
  const [picking, setPicking] = useState(false);

  const refresh = useCallback(() => {
    return specsApi.listEdges(draftId).then((edges) => {
      setOutgoing(edges.outgoing.filter((e) => e.edgeType === 'satisfies'));
      setIncoming(edges.incoming.filter((e) => e.edgeType === 'satisfies'));
    });
  }, [draftId]);

  useEffect(() => {
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-6 py-2 text-xs text-slate-400">
        <Spinner size="sm" /> Loading links…
      </div>
    );
  }

  // Design → the requirements it satisfies (editable)
  if (docType === 'design') {
    return (
      <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Satisfies</span>
          <button
            onClick={() => setPicking(true)}
            className="ml-auto inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:underline"
          >
            <Plus className="w-3.5 h-3.5" /> Link requirement
          </button>
        </div>
        {outgoing.length === 0 ? (
          <p className="text-xs text-slate-400">
            Not linked to any requirement yet. Link the requirements this design satisfies.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {outgoing.map((e) => (
              <span
                key={e.edgeId}
                title={e.needsReview ? 'This requirement changed after the design was last updated — review needed' : undefined}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full border pl-2.5 pr-1 py-1 text-xs',
                  e.needsReview
                    ? 'border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20'
                    : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900',
                )}
              >
                {e.needsReview && <AlertTriangle className="w-3 h-3 text-amber-500 shrink-0" />}
                <Link
                  to={`/requirements/${e.targetSpecId}`}
                  className="text-slate-700 dark:text-slate-300 hover:underline"
                >
                  {e.targetName || e.targetSpecId}
                </Link>
                <button
                  onClick={async () => { await specsApi.deleteEdge(draftId, e.edgeId); refresh(); }}
                  className="text-slate-400 hover:text-red-500 rounded-full p-0.5"
                  aria-label="Remove link"
                >
                  <X className="w-3 h-3" />
                </button>
              </span>
            ))}
          </div>
        )}
        {outgoing.some((e) => e.needsReview) && (
          <p className="mt-2 inline-flex items-center gap-1 text-[11px] text-amber-600 dark:text-amber-400">
            <AlertTriangle className="w-3 h-3" />
            A linked requirement changed after this design was last updated — review it.
          </p>
        )}
        {picking && (
          <RequirementPicker
            linkedIds={outgoing.map((e) => e.targetSpecId!).filter(Boolean)}
            onClose={() => setPicking(false)}
            onPick={async (reqId) => {
              await specsApi.createSatisfiesEdge(draftId, reqId);
              await refresh();
              setPicking(false);
            }}
          />
        )}
      </div>
    );
  }

  // Requirement → the design docs that satisfy it (read-only)
  if (docType === 'requirement') {
    if (incoming.length === 0) return null; // no clutter when nothing links here
    return (
      <div className="px-6 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900/40">
        <div className="flex items-center gap-2 mb-2">
          <Link2 className="w-3.5 h-3.5 text-slate-400" />
          <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Satisfied by</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {incoming.map((e) => (
            <Link
              key={e.edgeId}
              to={`/design/${e.sourceSpecId}`}
              className="inline-flex items-center rounded-full border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-2.5 py-1 text-xs text-slate-700 dark:text-slate-300 hover:underline"
            >
              {e.sourceName || e.sourceSpecId}
            </Link>
          ))}
        </div>
      </div>
    );
  }

  return null;
}

function RequirementPicker({
  linkedIds, onPick, onClose,
}: { linkedIds: string[]; onPick: (reqId: string) => void; onClose: () => void }) {
  const [requirements, setRequirements] = useState<SpecSummary[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    specsApi.listSpecs({ docType: 'requirement' })
      .then(setRequirements)
      .finally(() => setLoading(false));
  }, []);

  const linked = new Set(linkedIds);
  const matches = requirements
    .filter((r) => !linked.has(r.draftId))
    .filter((r) => r.name.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="mt-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 p-3">
      <div className="flex items-center gap-2 mb-2">
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search requirements…"
          className="flex-1 px-2.5 py-1.5 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 text-sm"
        />
        <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
      </div>
      {loading ? (
        <p className="text-xs text-slate-400 py-2">Loading requirements…</p>
      ) : matches.length === 0 ? (
        <p className="text-xs text-slate-400 py-2">
          {requirements.length === 0 ? 'No requirements yet.' : 'No matching requirements.'}
        </p>
      ) : (
        <div className="max-h-52 overflow-y-auto space-y-1">
          {matches.map((r) => (
            <button
              key={r.draftId}
              onClick={() => onPick(r.draftId)}
              className="flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
            >
              <Plus className="w-3.5 h-3.5 text-slate-400 shrink-0" />
              <span className="truncate">{r.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
